import { useState, type FormEvent } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CalendarDays, CheckCircle2, FolderKanban, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import {
  enderecoDoProjetoDetalhe,
  SITUACOES_DE_PROJETO,
  situacaoDeProjeto,
  type StatusDeProjeto,
} from '@/features/projetos/navegacao';
import { listarProjetos, criarProjeto, criarTarefa, excluirProjeto, TEMPLATES_DE_PROJETO, type LinhaDeProjeto } from '@/features/projetos/projetos.api';
import { cn } from '@/lib/cn';

const CHAVES = {
  lista: (status?: StatusDeProjeto | '') => ['projetos', 'lista', status] as const,
};

const STATUS_BAR: Record<string, string> = {
  DRAFT: 'bg-muted-foreground/30',
  PLANNING: 'bg-warning',
  ACTIVE: 'bg-primary',
  ON_HOLD: 'bg-warning/60',
  COMPLETED: 'bg-green-500',
  CANCELLED: 'bg-destructive/50',
};

type OrdemProjeto = 'nome' | 'progresso' | 'status' | 'data';

function ordenar(items: LinhaDeProjeto[], ordem: OrdemProjeto): LinhaDeProjeto[] {
  return [...items].sort((a, b) => {
    if (ordem === 'nome') return a.name.localeCompare(b.name, 'pt-BR');
    if (ordem === 'progresso') return (b.progressPercent ?? 0) - (a.progressPercent ?? 0);
    if (ordem === 'status') return a.status.localeCompare(b.status);
    if (ordem === 'data') return (b.finishAt ?? 0) - (a.finishAt ?? 0);
    return 0;
  });
}

export function ProjetosPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<StatusDeProjeto | ''>('');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [ordem, setOrdem] = useState<OrdemProjeto>('data');
  const [criando, setCriando] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');
  const [templateId, setTemplateId] = useState<string>('');

  const consulta = useQuery({
    queryKey: CHAVES.lista(status),
    queryFn: () =>
      listarProjetos({
        ...(status ? { status } : {}),
        ...(buscaAplicada ? { q: buscaAplicada } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  const mutation = useMutation({
    mutationFn: async (nome: string) => {
      const projeto = await criarProjeto({ name: nome });
      if (templateId) {
        const template = TEMPLATES_DE_PROJETO.find(t => t.id === templateId);
        if (template) {
          for (const task of template.tasks) {
            await criarTarefa(projeto.id, { name: task.name, durationMinutes: task.durationMinutes });
          }
        }
      }
      return projeto;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projetos'] });
      setNomeNovo('');
      setTemplateId('');
      setCriando(false);
    },
  });

  const submeterBusca = (e: FormEvent) => {
    e.preventDefault();
    setBuscaAplicada(busca);
  };

  const submeterNovo = (e: FormEvent) => {
    e.preventDefault();
    if (!nomeNovo.trim()) return;
    mutation.mutate(nomeNovo.trim());
  };

  return (
    <div className="mx-auto max-w-[1400px] space-y-4">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Projetos</h1>
        <Button onClick={() => setCriando(true)} size="sm" icon={<Plus className="h-4 w-4" aria-hidden />}>
          Novo projeto
        </Button>
      </header>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={submeterBusca} className="flex gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar projeto…"
              className="h-8 rounded-lg border border-input/80 bg-muted/40 pl-8 pr-3 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
            />
          </div>
          <Button type="submit" size="sm" variant="secondary">Buscar</Button>
        </form>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusDeProjeto | '')}
          className="h-8 rounded-lg border border-input/80 bg-muted/40 px-2 text-sm transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
        >
          <option value="">Todas as situações</option>
          {SITUACOES_DE_PROJETO.map((s) => (
            <option key={s.valor} value={s.valor}>{s.rotulo}</option>
          ))}
        </select>

        <select
          value={ordem}
          onChange={(e) => setOrdem(e.target.value as OrdemProjeto)}
          className="h-8 rounded-lg border border-input/80 bg-muted/40 px-2 text-sm transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
        >
          <option value="data">Ordenar: por prazo</option>
          <option value="progresso">Ordenar: por progresso</option>
          <option value="nome">Ordenar: A → Z</option>
          <option value="status">Ordenar: por situação</option>
        </select>
      </div>

      {/* Modal novo projeto */}
      {criando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl border border-border bg-background p-6 shadow-2xl">
            <h2 className="mb-4 text-base font-semibold">Novo projeto</h2>
            <form onSubmit={submeterNovo} className="flex flex-col gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Modelo (opcional)</label>
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="h-9 w-full rounded-lg border border-input/80 bg-muted/40 px-3 text-sm focus-visible:outline-none focus-visible:border-ring/40"
                >
                  <option value="">Nenhum (projeto em branco)</option>
                  {TEMPLATES_DE_PROJETO.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} — {t.description}</option>
                  ))}
                </select>
              </div>
              <input
                autoFocus
                value={nomeNovo}
                onChange={(e) => setNomeNovo(e.target.value)}
                placeholder="Nome do projeto"
                className="h-9 rounded-lg border border-input/80 bg-muted/40 px-3 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
              />
              {mutation.error && (
                <p className="text-xs text-destructive">Erro ao criar projeto. Tente novamente.</p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => { setCriando(false); setNomeNovo(''); }}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary" size="sm" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Criando…' : 'Criar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista */}
      {consulta.isPending ? (
        <SkeletonRegion>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="rounded-xl border border-border p-4 space-y-3">
                <SkeletonText className="h-4 w-3/4" />
                <SkeletonText className="h-3 w-1/2" />
                <SkeletonText className="h-2 w-full" />
              </div>
            ))}
          </div>
        </SkeletonRegion>
      ) : consulta.isError ? (
        <ErrorState error={consulta.error} onRetry={() => void consulta.refetch()} />
      ) : consulta.data?.items.length === 0 ? (
        <EmptyState
          icon={<FolderKanban className="h-8 w-8 text-muted-foreground" />}
          title="Nenhum projeto"
          description="Crie o primeiro projeto com o botão acima."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ordenar(consulta.data?.items ?? [], ordem).map((p) => (
            <CardDeProjeto key={p.id} projeto={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function CardDeProjeto({ projeto }: { projeto: LinhaDeProjeto }) {
  const qc = useQueryClient();
  const situacao = situacaoDeProjeto(projeto.status);
  const [confirmando, setConfirmando] = useState(false);

  const excluir = useMutation({
    mutationFn: () => excluirProjeto(projeto.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projetos'] }),
  });

  const pct = projeto.progressPercent ?? 0;
  const done = projeto.tasksDone ?? 0;
  const total = projeto.tasksTotal ?? 0;

  function fmtData(ep: number | null) {
    if (!ep) return null;
    return new Date(ep * 60 * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  }

  return (
    <div className="group relative flex flex-col rounded-xl border border-border bg-background transition-shadow hover:shadow-md">
      {/* Barra de cor de status no topo */}
      <div className={cn('h-1 w-full rounded-t-xl', STATUS_BAR[projeto.status] ?? 'bg-muted')} />

      <div className="flex flex-1 flex-col gap-3 p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-mono text-[10px] text-muted-foreground">{projeto.number}</p>
            <Link
              {...enderecoDoProjetoDetalhe(projeto.id)}
              className="block text-sm font-semibold text-foreground hover:text-primary leading-snug"
            >
              {projeto.name}
            </Link>
          </div>
          <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium', situacao.cor, 'bg-muted/50')}>
            {situacao.rotulo}
          </span>
        </div>

        {/* Descrição */}
        {projeto.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{projeto.description}</p>
        )}

        {/* Barra de progresso */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">
              {total > 0 ? `${done}/${total} tarefas` : 'Sem tarefas'}
            </span>
            <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
              {pct}%
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                pct === 100 ? 'bg-green-500' : 'bg-primary',
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-auto">
          <div className="flex items-center gap-3">
            {projeto.startAt && (
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {fmtData(projeto.startAt)}
              </span>
            )}
            {projeto.finishAt && (
              <span className="flex items-center gap-1">
                →
                {fmtData(projeto.finishAt)}
              </span>
            )}
            {pct === 100 && (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle2 className="h-3 w-3" /> Concluído
              </span>
            )}
          </div>

          {/* Excluir (apenas no hover) */}
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            {confirmando ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => excluir.mutate()}
                  disabled={excluir.isPending}
                  className="text-[10px] text-destructive hover:underline disabled:opacity-50"
                >
                  {excluir.isPending ? '…' : 'Confirmar'}
                </button>
                <span className="text-muted-foreground">·</span>
                <button onClick={() => setConfirmando(false)} className="text-[10px] hover:underline">
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmando(true)}
                className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                title="Excluir projeto"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
